<template>
    <v-dialog v-model="dialogSwitch" persistent scrollable width="75%">
        <v-card>
            <v-card-title id="heading" class="d-flex justify-space-between">
                <h4>{{ taskDetails.label }}'s Details</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="$emit('update:modelValue',false)"/>
            </v-card-title>

            <v-container>
                <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                         density="compact" variant="tonal">
                    <p v-for="error in errors">{{ error }}</p>
                </v-alert>

                <v-row>
                    <v-col class="py-0" cols="12">
                        <h4>Order Details</h4>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col v-if="task.maintenance_task" class="py-0 d-flex" cols="12">
                        Linked Maintenance Order:&nbsp;<b>{{ task.maintenance_task?.label }}</b>
                    </v-col>
                    <v-col v-if="task.inspection_task" class="py-0 d-flex" cols="12">
                        From Scheduled Inspection:&nbsp;<b>{{ task.inspection_task?.label }}</b>
                    </v-col>
                    <v-col class="py-1" cols="6">
                        <v-text-field v-model="taskData['label']" :rules="[vRules.required]"
                                      density="compact" hide-details label="Order Name"/>
                    </v-col>
                    <v-col class="py-1" cols="6">
                        <v-select v-model="taskData['status']" :items="statuses" :rules="[vRules.required]"
                                  density="compact" hide-details label="Status"/>
                    </v-col>
                    <v-col class="py-1" cols="6">
                        <v-select v-model="taskData['driver_id']" :items="drivers"
                                  density="compact" hide-details :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()" label="Driver"
                                  item-value="id"/>
                    </v-col>
                    <v-col v-if="task.type === 1" class="py-1" cols="6">
                        <v-text-field v-model.number="taskData['repeat_days']" density="compact" min="0"
                                      type="number" label="Repeat every (days)"
                                      hint="Blank or 0 for a one-off. Completing this schedules the next one."
                                      persistent-hint/>
                    </v-col>
                    <v-col v-if="task.type === 0" class="py-1" cols="6">
                        <v-select v-if="!serviceCenterDialog"
                                  v-model="taskData['vehicle_service_center_id']" :items="serviceCenters"
                                  density="compact" hide-details item-title="name" item-value="id"
                                  label="Service Center">
                            <template v-slot:append>
                                <v-icon class="cursor-pointer" icon="mdi-plus" size="small"
                                        @click="serviceCenterDialog = true"/>
                            </template>
                        </v-select>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="py-0 my-3" cols="12">
                        <div class="d-flex justify-space-between">
                            <h4>Task List</h4>
                            <v-btn color="black" icon="mdi-plus" size="x-small" variant="flat" @click="addItem"/>
                        </div>
                        <v-table fixed-header style="max-height: 250px; overflow: auto">
                            <thead>
                            <tr>
                                <th class="font-weight-bold">{{ task.type === 0 ? 'Maintenance' : 'Inspection' }} Task
                                </th>
                                <th class="font-weight-bold">Driver</th>
                                <th class="font-weight-bold">Notes</th>
                                <th class="font-weight-bold">Status</th>
                                <th class="font-weight-bold">Resolution</th>
                                <template v-if="!task.type">
                                    <th class="font-weight-bold">Parts Cost</th>
                                    <th class="font-weight-bold">Labour Cost</th>
                                    <th class="font-weight-bold">Other Cost</th>
                                </template>
                                <th class="font-weight-bold">Tax</th>
                                <th class="font-weight-bold">Cost</th>
                                <th class="font-weight-bold">{{ task.type === 0 ? 'Maintenance' : 'Inspection' }} Date
                                </th>
                                <th class="font-weight-bold">Action</th>
                            </tr>
                            </thead>
                            <tbody class="text-caption">
                            <tr v-if="newTaskItem">
                                <td class="pa-0">
                                    <v-autocomplete v-model="newTaskItem['vehicle_service_type_id']"
                                                    :items="services"
                                                    :label="`${task.type === 0 ? 'Maintenance' : 'Inspection'} Task`"
                                                    :rules="[vRules.required]" density="compact"
                                                    hide-details item-title="name" item-value="id"/>
                                </td>
                                <td class="pa-0" colspan="2">
                                    <v-text-field v-model="newTaskItem['notes']" density="compact"
                                                  hide-details label="Initial Notes"/>
                                </td>
                                <td class="d-flex align-center">
                                    <v-btn icon="mdi-content-save" size="small" variant="text" @click="addTaskItem"/>
                                    <v-btn color="black" icon="mdi-close" size="x-small" variant="text"
                                           @click="newTaskItem = null"/>
                                </td>
                            </tr>
                            <tr v-for="(item,index) in taskItems" :key="index">
                                <template v-if="selectedTaskItem?.id === item.id">
                                    <td class="pa-0">{{ getService(item.vehicle_service_type_id)?.name }}</td>
                                    <td class="pa-0">
                                        <v-select v-model="formData['assigned_driver_id']" :items="drivers"
                                                  density="compact" hide-details :item-title="d => `${d.first_name || ''} ${d.last_name || ''}`.trim()"
                                                  item-value="id"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-text-field v-model="formData['notes']" density="compact" hide-details/>
                                    </td>
                                    <td class="pa-0">
                                        <v-select v-model="formData['status']" :items="statuses"
                                                  :rules="[vRules.required]" density="compact" hide-details/>
                                    </td>
                                    <td class="pa-0">
                                        <v-select v-if="formData['status'] === 3" v-model="formData['resolution']"
                                                  :items="resolutions" :rules="[vRules.required]" density="compact"
                                                  hide-details/>
                                    </td>
                                    <template v-if="!task.type">
                                        <td class="pa-0">
                                            <v-text-field v-model="formData['parts_cost']" density="compact"
                                                          hide-details min="0.1" type="number"/>
                                        </td>
                                        <td class="pa-0">
                                            <v-text-field v-model="formData['labour_cost']" density="compact"
                                                          hide-details min="0.1" type="number"/>
                                        </td>
                                        <td class="pa-0">
                                            <v-text-field v-model="formData['other_cost']" density="compact"
                                                          hide-details min="0.1" type="number"/>
                                        </td>
                                    </template>
                                    <td class="pa-0">
                                        <v-text-field v-model="formData['tax']" density="compact" hide-details
                                                      min="0.1" type="number"/>
                                    </td>
                                    <td v-if="!task.type" class="pa-0 font-weight-bold text-center">{{
                                            sumCost(formData)
                                        }}
                                    </td>
                                    <td v-else class="pa-0">
                                        <v-text-field v-model="formData['cost']" density="compact" hide-details
                                                      min="0.1" type="number"/>
                                    </td>
                                    <td class="pa-0">
                                        <v-date-input
                                            v-model="formData['service_date']" density="compact"
                                            :rules="[vRules.required]" hide-actions prepend-icon=""
                                            prepend-inner-icon="$calendar"/>
                                    </td>
                                    <td class="pa-0 d-flex align-center">
                                        <v-btn icon="mdi-content-save" size="small" variant="text"
                                               @click="updateTaskItem"/>
                                        <v-btn color="black" icon="mdi-close" size="x-small" variant="text"
                                               @click="selectedTaskItem = null"/>
                                    </td>
                                </template>
                                <template v-else>
                                    <td>{{ getService(item.vehicle_service_type_id)?.name }}</td>
                                    <td>{{ getDriverName(item.assigned_driver_id) }}</td>
                                    <td>
                                        {{ item.notes }}
                                        <div v-if="item.inspection" class="text-caption text-success">
                                            <v-icon icon="mdi-clipboard-check-outline" size="14"/>
                                            Done by driver inspection: {{ item.inspection.type }}-trip
                                            <template v-if="item.inspection.driver">by {{ item.inspection.driver }}</template>
                                            {{ formatDateTime(item.inspection.submitted_at) }}
                                            ({{ item.inspection.status }})
                                        </div>
                                    </td>
                                    <td>{{ item.status_title }}</td>
                                    <td>{{ item.resolution_title }}</td>
                                    <template v-if="!task.type">
                                        <td>{{ item.parts_cost }}</td>
                                        <td>{{ item.labour_cost }}</td>
                                        <td>{{ item.other_cost }}</td>
                                    </template>
                                    <td>{{ item.tax }}</td>
                                    <td v-if="!task.type" class="font-weight-bold">{{ sumCost(item) }}</td>
                                    <td v-else>{{ item.cost }}</td>
                                    <td>{{
                                            item.service_date ? formatDateTime(item.service_date) : null
                                        }}
                                    </td>
                                    <td>
                                        <v-btn v-if="item.status !== 3" color="black" icon="mdi-pencil-outline"
                                               size="small" variant="text" @click="selectItem(item)"/>
                                    </td>
                                </template>
                            </tr>
                            </tbody>
                        </v-table>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col cols="12">
                        <h4>Payment Details</h4>
                        <v-checkbox v-model="taskData['not_paid']" class="" hide-details label="Not Paid"/>
                    </v-col>
                    <v-col class="py-1" cols="6">
                        <v-select v-model="taskData['driver_payment_type_id']" :items="driverPaymentTypes"
                                  density="compact" hide-details item-title="name" item-value="id"
                                  :disabled="taskData['not_paid']" :rules="[vRules.required]" label="Payment Type"/>
                    </v-col>
                    <v-col class="py-1" cols="6">
                        <v-text-field v-model="taskData['receipt_id']" density="compact" hide-details
                                      :disabled="taskData['not_paid']" :rules="[vRules.required]" label="Receipt ID"/>
                    </v-col>
                    <template v-if="!task.type">
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['parts_cost']" density="compact" hide-details
                                          label="Total Parts Cost" readonly type="number"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['labour_cost']" density="compact" hide-details
                                          label="Total Labour Cost" readonly type="number"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['other_cost']" density="compact" hide-details
                                          label="Total Other Cost" readonly type="number"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['tax']" density="compact" hide-details label="Total Tax"
                                          readonly type="number"/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field :model-value="totals['total_cost']" density="compact" hide-details
                                          label="Total Cost" readonly/>
                        </v-col>
                    </template>
                    <template v-else>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['tax']" density="compact" hide-details label="Total Tax"
                                          readonly/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field v-model="totals['cost']" density="compact" hide-details label="Total Cost"
                                          readonly/>
                        </v-col>
                        <v-col class="py-1" cols="6">
                            <v-text-field :model-value="totals['total_cost']" density="compact" hide-details
                                          label="Grand Total" readonly/>
                        </v-col>
                    </template>
                    <v-col class="py-1 text-caption" cols="6">
                        <p><b>Updated by:</b> {{ task.updated_by_user }}</p>
                        <p><b>Last updated:</b> {{ formatDateTime(task.updated_at) }}</p>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn v-if="failedTasksCount && !task.inspection_task" color="red" size="small" variant="outlined"
                       @click="createLinkedMaintenanceTask">
                    Create Maintenance Task
                </v-btn>
                <v-btn :disabled="!!selectedTaskItem || !!newTaskItem || !isDirty" color="black" size="small"
                       variant="outlined" @click="updateOrder">Update
                </v-btn>
                <v-btn size="small" type="button" variant="outlined"
                       @click="$emit('update:modelValue',false)">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>


    <v-dialog v-model="serviceCenterDialog" persistent scrollable width="350">
        <v-card>
            <v-card-title class="d-flex justify-space-between">
                <h4>New Service Center</h4>
                <v-btn density="compact" icon="mdi-close" variant="flat" @click="serviceCenterDialog =false"/>
            </v-card-title>

            <v-container>
                <v-alert v-if="errors.length" class="mb-3 bg-error" closable close-label="Close Alert"
                         density="compact" variant="tonal">
                    <p v-for="error in errors">{{ error }}</p>
                </v-alert>

                <v-row>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['name']" :rules="[vRules.required]"
                                      density="compact" hide-details label="Name"/>
                    </v-col>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['phone']" density="compact" hide-details
                                      label="Phone"/>
                    </v-col>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['email']" density="compact" hide-details
                                      label="Email"/>
                    </v-col>
                    <v-col class="py-1" cols="12">
                        <v-text-field v-model="serviceCenterData['address']" density="compact" hide-details
                                      label="Address"/>
                    </v-col>
                </v-row>
            </v-container>

            <v-card-actions class="px-4 d-flex justify-end">
                <v-btn color="black" size="small" variant="outlined" @click="storeServiceCenter">Save</v-btn>
                <v-btn size="small" type="button" variant="outlined"
                       @click="serviceCenterDialog=false">
                    Close
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import {useDate} from 'vuetify'
import {refreshMenuCounts} from "@/composables/useMenuCounts";
import {send} from "@/composables/apiClient";
import {computed, onMounted, reactive, ref} from "vue";
import {useSnackbarStore} from "@/stores/snackBarStore";
import {formatDateTime} from "@/composables/useTimezone";
import {VDateInput} from "vuetify/lib/labs/VDateInput";

const snackbar = useSnackbarStore();
const props = defineProps({
    driverPaymentTypes: Array,
    serviceCenters: Array,
    modelValue: Boolean,
    services: Array,
    drivers: Array,
    asset: Object,
    task: Object,
});
const emits = defineEmits(['update:modelValue', 'updateTask', 'addServiceCenter', 'createLinkedMaintenanceTask', 'itemsChanged']);
const dialogSwitch = ref(props.modelValue);
const newTaskItem = ref(null);
const selectedTaskItem = ref(null);
const serviceCenterDialog = ref(false);
const taskDetails = ref(props.task);
const taskItems = ref([]);
const errors = ref([]);
const statuses = [
    {value: 1, title: "Pending"},
    {value: 2, title: "Skipped"},
    {value: 3, title: "Completed"},
]
const resolutions = [
    {value: 1, title: "Success"},
    {value: 3, title: "Failed"},
]
if (props.task.type === 0) {
    resolutions.push(
        {value: 2, title: "Skipped"},
        {value: 4, title: "Repaired"},
        {value: 5, title: "Replaced"})
}
const formData = reactive({
    vehicle_service_type_id: null,
    assigned_driver_id: null,
    service_date: null,
    resolution: null,
    status: null,
    cost: null,
    parts_cost: null,
    labour_cost: null,
    other_cost: null,
    tax: null,
});
const taskData = reactive({
    label: props.task.label ?? null,
    status: props.task.status ?? null,
    driver_id: props.task.driver_id ?? null,
    receipt_id: props.task.receipt_id ?? null,
    not_paid: !props.task.driver_payment_type_id ?? false,
    driver_payment_type_id: props.task.driver_payment_type_id ?? null,
    vehicle_service_center_id: props.task.vehicle_service_center_id ?? null,
    ...(props.task.type === 1 ? {repeat_days: props.task.repeat_days ?? null} : {}),
})
const serviceCenterData = reactive({
    company_id: props.asset.company_id,
    name: null,
    address: null,
    phone: null,
    email: null,
});
const adapter = ref(null);

onMounted(() => {
    props.task['not_paid'] = taskData['not_paid'];
    adapter.value = useDate();
    fetchTaskItems();
})

const isDirty = computed(() => {
    return Object.keys(taskData).some(key => taskData[key] !== props.task[key])
})
// A blank or cleared field counts as 0. parseFloat('') is NaN, which turned
// the whole line total into "NaN".
const num = (value) => {
    const n = parseFloat(value);
    return Number.isFinite(n) ? n : 0;
}
const sumCost = (data) => {
    const cost = num(data['parts_cost']) + num(data['labour_cost']) + num(data['other_cost']) + num(data['tax'])
    return cost.toFixed(2)
}
const totals = computed(() => {
    const total = {
        tax: 0,
        cost: 0,
        parts_cost: 0,
        labour_cost: 0,
        other_cost: 0,
        total_cost: 0,
    }
    taskItems.value.map((item) => {
        total.tax += num(item.tax);
        // Not gated on tax: an inspection line with no tax still costs.
        total.cost += num(item.cost);
        total.parts_cost += num(item.parts_cost);
        total.labour_cost += num(item.labour_cost);
        total.other_cost += num(item.other_cost);
    })
    total.tax = parseFloat(total.tax).toFixed(2);
    total.cost = parseFloat(total.cost).toFixed(2);
    total.parts_cost = parseFloat(total.parts_cost).toFixed(2);
    total.labour_cost = parseFloat(total.labour_cost).toFixed(2);
    total.other_cost = parseFloat(total.other_cost).toFixed(2);

    if (props.task.type) {
        total.total_cost = (parseFloat(total.tax) + parseFloat(total.cost)).toFixed(2);
    } else {
        total.total_cost = (parseFloat(total.tax) + parseFloat(total.parts_cost) + parseFloat(total.labour_cost) + parseFloat(total.other_cost)).toFixed(2);
    }
    return total;
})
const failedTasksCount = computed(() => {
    let totalCount = 0;
    taskItems.value.map((item) => {
        totalCount += item.resolution === 3 ? 1 : 0;
    })
    return totalCount;
})

const createLinkedMaintenanceTask = () => {
    emits('createLinkedMaintenanceTask', props.task);
    emits('update:modelValue', false);
}
const addItem = () => {
    selectedTaskItem.value = null;
    newTaskItem.value = {
        vehicle_service_type_id: null,
        note: null,
    };
}
const selectItem = (item) => {
    // const adapter = useDate();
    selectedTaskItem.value = item;
    let serviceDate = null;
    if (selectedTaskItem.value?.service_date) {
        serviceDate = (new Date(selectedTaskItem.value?.service_date)).toISOString().split('T')[0];
        serviceDate = adapter.value.parseISO(serviceDate);
    }
    formData['service_date'] = serviceDate ?? null;
    formData['vehicle_service_type_id'] = selectedTaskItem.value?.vehicle_service_type_id ?? null;
    formData['assigned_driver_id'] = selectedTaskItem.value?.assigned_driver_id ?? null;
    formData['resolution'] = selectedTaskItem.value?.resolution ?? null;
    formData['status'] = selectedTaskItem.value?.status ?? null;
    formData['notes'] = selectedTaskItem.value?.notes ?? null;
    formData['cost'] = selectedTaskItem.value?.cost ?? null;
    formData['tax'] = selectedTaskItem.value?.tax ?? null;
    if (!props.task.type) {
        formData['parts_cost'] = selectedTaskItem.value?.parts_cost ?? null;
        formData['labour_cost'] = selectedTaskItem.value?.labour_cost ?? null;
        formData['other_cost'] = selectedTaskItem.value?.other_cost ?? null;
    }
}
const getService = (id) => {
    return props.services.find((record) => record.id === id)
}
const getDriver = (id) => {
    return props.drivers.find((record) => record.id === id)
}
const getDriverName = (id) => {
    const d = getDriver(id);
    if (!d) return '';
    const first = d.user?.first_name || d.user_first_name || d.first_name || '';
    const last = d.user?.last_name || d.user_last_name || d.last_name || '';
    return (first + ' ' + last).trim();
}
const getPaymentType = (id) => {
    return props.driverPaymentTypes.find((record) => record.id === id)
}
const fetchTaskItems = () => {
    send('get', `fleet/service-tasks/${props.task.id}/items`)
        .then((response) => {
            if (response.data.success) {
                taskItems.value = response.data.data ?? [];
            }
        })
        .catch(function (error) {
            console.log(error)
            snackbar.showSnackbar('Something went wrong', 'error');
        })
}
const storeServiceCenter = () => {
    errors.value = [];
    if (!serviceCenterData['name']) {
        errors.value.push("Name field is required");
        return;
    }

    send('post', `fleet/service-centers`, serviceCenterData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                emits('addServiceCenter', response.data.data);
                serviceCenterDialog.value = false;
                serviceCenterData['name'] = null;
                serviceCenterData['phone'] = null;
                serviceCenterData['email'] = null;
                serviceCenterData['address'] = null;
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
const updateOrder = () => {
    errors.value = [];
    if (!taskData['not_paid'] && (!taskData['driver_payment_type_id'] || !taskData['receipt_id'])) {
        errors.value.push("Payment & Receipt ID are required fields");
        return;
    }

    send('put', `fleet/service-tasks/${props.task.id}`, taskData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                taskDetails.value = response.data.data;
                emits('updateTask', taskDetails.value);
                emits('update:modelValue', false);
                refreshMenuCounts();
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
const addTaskItem = () => {
    errors.value = [];
    if (!newTaskItem.value.vehicle_service_type_id) {
        errors.value.push(`${props.task.type === 0 ? 'Maintenance' : 'Inspection'} Task field is required`);
        return;
    }
    send('post', `fleet/service-tasks/${props.task.id}/items`, newTaskItem.value)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                taskItems.value.unshift(response.data.data);
                newTaskItem.value = null;
                refreshMenuCounts();
                emits('itemsChanged');
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}
const updateTaskItem = () => {
    errors.value = [];
    if (!formData['assigned_driver_id']) {
        errors.value.push("Driver field is required");
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        return;
    }
    if (!formData['service_date']) {
        errors.value.push(`${props.task.type === 0 ? 'Maintenance' : 'Inspection'} Date is required`);
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        return;
    }
    if (formData['status'] === 3 && !formData['resolution']) {
        errors.value.push(`Resolution field is required`);
        document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        return;
    }
    send('put', `fleet/service-tasks/${selectedTaskItem.value.id}/items`, formData)
        .then((response) => {
            snackbar.showSnackbar(response.data.message);
            if (response.data.success) {
                taskItems.value.map((record, index) => {
                    if (record.id === selectedTaskItem.value.id) {
                        taskItems.value[index] = response.data.data;
                    }
                });
                selectedTaskItem.value = null;
                checkCompletedItemsAndMarkOrderCompleted();
                refreshMenuCounts();
                emits('itemsChanged');
            }
        })
        .catch(function (errorResponse) {
            if (errorResponse.response.data.errors) {
                let validationErrors = errorResponse.response.data.errors;
                Object.keys(validationErrors).forEach((key) => {
                    errors.value.push(validationErrors[key][0]);
                });
            } else if (errorResponse.response.data.message) {
                errors.value.push(errorResponse.response.data.message);
            }
            snackbar.showSnackbar('Something went wrong', 'error');
            document.getElementById('heading').scrollIntoView({behavior: 'smooth'});
        })
}

const checkCompletedItemsAndMarkOrderCompleted = () => {
    const pendingTasks = taskItems.value.filter((record, index) => {
        return record.status === 1;
    });
    if (!pendingTasks.length) {
        if (!confirm("Do you want to mark this order complete?")) {
            return;
        }
        taskData['status'] = 3;
        updateOrder();
    }
}
</script>

<style>
.append-btn .v-input__append {
    margin: 0;
}
</style>
